import React, { useMemo, useRef, useState } from 'react';
import { FiTrash2, FiX } from 'react-icons/fi';
import { useDialogFocus } from '../../hooks/useDialogFocus';
import { useI18n } from '../../i18n';
import {
  profitabilityService,
  type ChargeRate,
  type ProfitabilitySite,
} from '../../services/profitabilityService';
import { formatDate, formatMoney } from './profitabilityPresentation';

interface ChargeRateModalProps {
  contract: { id: string; name: string };
  /** The client's sites, for a rate that applies to one of them only. */
  sites: ProfitabilitySite[];
  /** Every rate the company holds; the dialog shows this client's. */
  chargeRates: ChargeRate[];
  currencyCode: string;
  today: string;
  /** Preselects a site when the dialog was opened from its row. */
  initialSiteId?: string | null;
  onClose: () => void;
  /** Called after a rate is saved or removed, so the page can price with it. */
  onChanged: () => Promise<void>;
}

const CONTRACT_SCOPE = '';

const parseAmount = (value: string): number | null => {
  const parsed = Number(value.replace(',', '.'));
  return value.trim() !== '' && Number.isFinite(parsed) ? parsed : null;
};

export default function ChargeRateModal({
  contract,
  sites,
  chargeRates,
  currencyCode,
  today,
  initialSiteId = null,
  onClose,
  onChanged,
}: ChargeRateModalProps) {
  const { t } = useI18n();
  const dialogRef = useRef<HTMLDivElement>(null);
  useDialogFocus(dialogRef);

  const [scope, setScope] = useState(initialSiteId ?? CONTRACT_SCOPE);
  const [hourlyRate, setHourlyRate] = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState(today);
  const [sundayMultiplier, setSundayMultiplier] = useState('1');
  const [publicHolidayMultiplier, setPublicHolidayMultiplier] = useState('1');
  const [note, setNote] = useState('');
  const [isBusy, setIsBusy] = useState(false);
  const [error, setError] = useState('');

  const siteNames = useMemo(() => new Map(sites.map((site) => [site.id, site.name])), [sites]);
  const rates = useMemo(
    () => chargeRates
      .filter((rate) => rate.clientContractId === contract.id)
      .sort((left, right) => (
        (left.siteId ? siteNames.get(left.siteId) ?? '' : '').localeCompare(right.siteId ? siteNames.get(right.siteId) ?? '' : '')
        || right.effectiveFrom.localeCompare(left.effectiveFrom)
      )),
    [chargeRates, contract.id, siteNames],
  );

  const run = async (action: () => Promise<void>) => {
    setIsBusy(true);
    setError('');
    try {
      await action();
      await onChanged();
    } catch (err) {
      setError((err as Error).message || t('Something went wrong'));
    } finally {
      setIsBusy(false);
    }
  };

  const handleSave = (event: React.FormEvent) => {
    event.preventDefault();
    const rate = parseAmount(hourlyRate);
    const sunday = parseAmount(sundayMultiplier);
    const holiday = parseAmount(publicHolidayMultiplier);

    if (rate === null || rate < 0) {
      setError(t('Enter the hourly charge rate.'));
      return;
    }
    if (sunday === null || holiday === null || sunday < 0 || holiday < 0) {
      setError(t('Enter a multiplier of 0 or more. 1 charges those hours at the plain rate.'));
      return;
    }
    if (!effectiveFrom) {
      setError(t('Choose the date the rate applies from.'));
      return;
    }

    void run(async () => {
      await profitabilityService.saveChargeRate({
        clientContractId: contract.id,
        siteId: scope === CONTRACT_SCOPE ? null : scope,
        effectiveFrom,
        hourlyRate: rate,
        sundayMultiplier: sunday,
        publicHolidayMultiplier: holiday,
        note: note.trim() || null,
      });
      setHourlyRate('');
      setNote('');
    });
  };

  return (
    <div className="modal-overlay" role="presentation">
      <div
        className="modal pf-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="pf-rate-modal-title"
        ref={dialogRef}
        tabIndex={-1}
      >
        <div className="modal__header">
          <h2 className="modal__title" id="pf-rate-modal-title">
            {t('Charge rates for {client}', { client: contract.name })}
          </h2>
          <button type="button" className="btn btn--icon" onClick={onClose} aria-label={t('Close')}>
            <FiX aria-hidden="true" />
          </button>
        </div>

        <p className="pf-modal__intro">
          {t('What this client pays for an hour of cover. A rate set for one site replaces the client rate at that site.')}
        </p>

        {error && <div className="msg--error" role="alert">{error}</div>}

        <div className="pf-table-wrap">
          <table className="pf-table pf-table--compact">
            <thead>
              <tr>
                <th>{t('Applies to')}</th>
                <th>{t('Effective')}</th>
                <th className="pf-table__num">{t('Hourly rate')}</th>
                <th className="pf-table__num">{t('Sunday')}</th>
                <th className="pf-table__num">{t('Public holiday')}</th>
                <th>{t('Note')}</th>
                <th aria-label={t('Actions')} />
              </tr>
            </thead>
            <tbody>
              {rates.length === 0 ? (
                <tr><td colSpan={7} className="pf-empty">{t('No charge rate recorded for this client yet.')}</td></tr>
              ) : rates.map((rate) => (
                <tr key={rate.id}>
                  <td>{rate.siteId ? siteNames.get(rate.siteId) ?? t('Unknown site') : t('Every site')}</td>
                  <td>
                    {rate.effectiveTo
                      ? t('{from} to {to}', { from: formatDate(rate.effectiveFrom), to: formatDate(rate.effectiveTo) })
                      : t('From {from}', { from: formatDate(rate.effectiveFrom) })}
                  </td>
                  <td className="pf-table__num">{formatMoney(rate.hourlyRate, currencyCode)}</td>
                  <td className="pf-table__num">×{rate.sundayMultiplier}</td>
                  <td className="pf-table__num">×{rate.publicHolidayMultiplier}</td>
                  <td className="pf-table__muted">{rate.note ?? '-'}</td>
                  <td className="pf-table__num">
                    <button
                      type="button"
                      className="btn btn--icon"
                      disabled={isBusy}
                      onClick={() => { void run(() => profitabilityService.deleteChargeRate(rate.id)); }}
                      aria-label={t('Remove this rate')}
                      title={t('Remove this rate. The rate before it runs on in its place.')}
                    >
                      <FiTrash2 aria-hidden="true" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <form className="pf-rate-form" onSubmit={handleSave}>
          <label className="form__group">
            <span className="form__label">{t('Applies to')}</span>
            <select className="form__select" value={scope} onChange={(event) => setScope(event.target.value)}>
              <option value={CONTRACT_SCOPE}>{t('Every site of this client')}</option>
              {sites.map((site) => <option key={site.id} value={site.id}>{site.name}</option>)}
            </select>
          </label>
          <label className="form__group">
            <span className="form__label">{t('Hourly charge rate')}</span>
            <input
              className="form__input"
              type="number"
              min="0"
              step="0.01"
              inputMode="decimal"
              value={hourlyRate}
              onChange={(event) => setHourlyRate(event.target.value)}
            />
          </label>
          <label className="form__group">
            <span className="form__label">{t('Effective from')}</span>
            <input
              className="form__input"
              type="date"
              value={effectiveFrom}
              onChange={(event) => setEffectiveFrom(event.target.value)}
            />
          </label>
          <label className="form__group">
            <span className="form__label">{t('Sunday multiplier')}</span>
            <input
              className="form__input"
              type="number"
              min="0"
              max="10"
              step="0.05"
              inputMode="decimal"
              value={sundayMultiplier}
              onChange={(event) => setSundayMultiplier(event.target.value)}
            />
          </label>
          <label className="form__group">
            <span className="form__label">{t('Public holiday multiplier')}</span>
            <input
              className="form__input"
              type="number"
              min="0"
              max="10"
              step="0.05"
              inputMode="decimal"
              value={publicHolidayMultiplier}
              onChange={(event) => setPublicHolidayMultiplier(event.target.value)}
            />
          </label>
          <label className="form__group pf-rate-form__note">
            <span className="form__label">{t('Note')}</span>
            <input
              className="form__input"
              type="text"
              maxLength={200}
              value={note}
              onChange={(event) => setNote(event.target.value)}
            />
          </label>
          <div className="pf-rate-form__actions">
            <span className="pf-rate-form__hint">
              {t('A new rate closes the one it replaces the day before. Saving the same date again corrects that rate.')}
            </span>
            <button type="submit" className="btn btn--primary" disabled={isBusy}>
              {isBusy ? t('Saving...') : t('Save rate')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
